import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CircleHelp, RotateCcw, Search, UserRound } from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useDebouncedValue } from '@utils/hooks/use-debounced-value.ts';
import { useSearchSpeakers } from '@utils/api/organizations/speakers';
import { Speaker } from '@utils/types/organization/speaker.ts';
import { VoiceIdentification } from '@utils/types/organization/voice-profile.ts';
import { VoiceSuggestionNote } from './voice-suggestion-note.tsx';
import { VoiceErrorMessage } from './voice-error-message.tsx';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The original diarization label being decided — not a display name.
   *  Null while the source timeline is still being fetched, or when the
   *  document has no diarization to key a decision on. */
  speakerLabel: string | null;
  /** The timeline request is in flight. */
  isResolvingLabel?: boolean;
  /** Set when this document can never support identity decisions. */
  unavailableReason?: 'no-diarization' | null;
  /** The current suggestion and human decision for that label. */
  identification?: VoiceIdentification;
  /** Resolve a directory id to a name for the "currently assigned" row. */
  resolveSpeakerName?: (speakerId: number) => string | undefined;
  onAssign: (speakerId: number | null, person?: Speaker) => Promise<void>;
  onClearOverride?: () => Promise<void>;
  isSaving?: boolean;
  error?: unknown;
  /** The dialog is the first step of a pick-and-enroll journey — show the user
   *  that enrollment comes next, so the Save-voice path never looks like a
   *  dead-end "pick a person then stop". */
  chainToEnroll?: boolean;
}

/** Choose the directory person behind one detected voice.
 *
 * Available before any voice sample or recognition suggestion exists, and
 * saving a choice here never enrolls biometric data — that is a separate,
 * admin-only action. The scope is stated in the dialog because it is not
 * obvious: the mapping applies to this detected voice across the whole
 * session, not just the row the user clicked. */
export const AssignPersonDialog = ({
  isOpen,
  onClose,
  speakerLabel,
  isResolvingLabel = false,
  unavailableReason = null,
  identification,
  resolveSpeakerName,
  onAssign,
  onClearOverride,
  isSaving = false,
  error,
  chainToEnroll = false
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isLoading } = useSearchSpeakers({
    search: debouncedSearch,
    limit: 50
  });

  const speakers = useMemo<Speaker[]>(() => data?.result ?? [], [data]);

  const currentId = identification?.override_set
    ? identification.override_speaker_id
    : (identification?.effective_speaker_id ?? null);

  // An explicit unknown is a decision, not an absence of one — show it as the
  // selected row so the user can tell the two states apart.
  const unknownChosen =
    identification?.override_set && identification.override_speaker_id === null;

  const describe = (speaker: Speaker) => {
    const parts = [speaker.role, speaker.party].filter(Boolean);
    return parts.join(' · ');
  };

  const rowBase =
    'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors cursor-pointer hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        chainToEnroll
          ? t('voice-choose-person-to-remember')
          : t('voice-assign-person')
      }
      caption={
        speakerLabel
          ? chainToEnroll
            ? t('voice-choose-person-to-remember-caption', {
                label: speakerLabel
              })
            : t('voice-assign-person-caption', { label: speakerLabel })
          : undefined
      }
      customWidth="min(520px, calc(100vw - 2rem))"
      customMaxHeight="calc(100dvh - 2rem)"
      footerActions={[
        <Button
          key="cancel"
          variant="secondary"
          onClick={onClose}
          label={t('cancel')}
          disabled={isSaving}
        />
      ]}
    >
      {/* This document's source has no speaker separation, so there is no
          label to key a decision on. Say that, rather than offering a picker
          whose choice could not be saved. */}
      {unavailableReason === 'no-diarization' ? (
        <Text as="p" size="sm" tone="muted">
          {t('voice-error-no-diarization')}
        </Text>
      ) : !speakerLabel ? (
        <Text as="p" size="sm" tone="muted">
          {isResolvingLabel ? t('loading') : t('voice-error-label-not-found')}
        </Text>
      ) : (
        <div className="space-y-4">
          {/* The assignment's scope, stated rather than implied. In the
              pick-and-enroll journey, also say the next step is enrollment. */}
          <Text as="p" size="sm" tone="muted">
            {chainToEnroll
              ? t('voice-choose-person-to-remember-explanation')
              : t('voice-assign-scope-explanation')}
          </Text>

          {identification && (
            <VoiceSuggestionNote
              identification={identification}
              resolveSpeakerName={resolveSpeakerName}
            />
          )}

          {/* `id` is what ties the atom's <label htmlFor> to this field. Without
            it the search box has no accessible name at all — a placeholder
            does not provide one. */}
          <Input
            id="voice-directory-search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={t('voice-search-directory')}
            label={
              <span className="flex items-center gap-2">
                <Search size={14} />
                {t('speaker-directory')}
              </span>
            }
            autoFocus
          />

          <div
            className="max-h-72 space-y-1 overflow-y-auto rounded-md border p-1"
            style={{ borderColor: getColour('border') }}
            role="listbox"
            aria-label={t('speaker-directory')}
          >
            {/* Explicit unknown: an answer the user is allowed to give. An
              ambiguous voice must stay usable without forcing a match. */}
            <button
              type="button"
              role="option"
              aria-selected={!!unknownChosen}
              className={rowBase}
              style={{
                backgroundColor: unknownChosen
                  ? getColour('secondLayer')
                  : 'transparent'
              }}
              disabled={isSaving}
              onClick={() => void onAssign(null)}
            >
              <span className="flex items-center gap-2">
                <CircleHelp size={16} />
                <span>
                  <span className="block font-medium">
                    {t('voice-unknown')}
                  </span>
                  <Text as="span" size="xs" tone="muted">
                    {t('voice-unknown-description')}
                  </Text>
                </span>
              </span>
              {unknownChosen && <Check size={16} aria-hidden />}
            </button>

            {isLoading && (
              <Text as="p" size="sm" tone="muted" className="px-3 py-2">
                {t('loading')}
              </Text>
            )}

            {!isLoading && speakers.length === 0 && (
              <Text as="p" size="sm" tone="muted" className="px-3 py-2">
                {t('voice-no-directory-matches')}
              </Text>
            )}

            {speakers.map(speaker => {
              const selected = !unknownChosen && currentId === speaker.id;
              const detail = describe(speaker);
              return (
                <button
                  key={speaker.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={rowBase}
                  style={{
                    backgroundColor: selected
                      ? getColour('secondLayer')
                      : 'transparent'
                  }}
                  disabled={isSaving}
                  onClick={() => void onAssign(speaker.id, speaker)}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <UserRound size={16} />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {speaker.name}
                      </span>
                      {detail && (
                        <Text as="span" size="xs" tone="muted">
                          {detail}
                        </Text>
                      )}
                    </span>
                  </span>
                  {selected && <Check size={16} aria-hidden />}
                </button>
              );
            })}
          </div>

          {/* Only offered once a human decision exists to clear. */}
          {identification?.override_set && onClearOverride && (
            <Button
              variant="secondary"
              size="small"
              icon={<RotateCcw width={14} />}
              label={t('voice-clear-decision')}
              onClick={() => void onClearOverride()}
              disabled={isSaving}
            />
          )}

          <VoiceErrorMessage error={error} />
        </div>
      )}
    </Modal>
  );
};
